import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CreatorContentReport, CreatorContentReportRow, CreatorContentTotals, ScoreBand } from '@discovr/types';
import { SoundUsedCell } from './SoundUsedCell';
import { AdMentionedCell, FirstFiveDaysCell } from './FirstFiveAdCells';
import { LivenessStatusPill } from './LivenessStatusCell';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ApiError } from '@discovr/common';
import { useCreatorContentReport, type CreatorContentParams } from '@discovr/features';
import { Play } from '../icons';
import { liveVideoThumbnailUrl } from '../lib/youtubeThumbnail';
import { DISPLAY_DECIMALS, formatCpv, formatIntegerIN, formatMoneyExact, moneyFitClass } from '../lib/format';
import { currentCount, describeCount, type CountDisplay, type CountKind } from '../lib/engagementCount';
import { HintTrigger } from '../components/common/HintTrigger';
import { COUNT_CELL_CLASS, COUNT_CELL_MUTED_CLASS, EngagementCountCell } from './EngagementCountCell';
import { PlatformCreatorCell } from '../lib/platformProfile';
import { VideoPlayerPopup } from './VideoPlayerPopup';
import { rosterSortStorageKey } from '../campaign-detail/hooks';
import { CreatorsControls } from '../campaign-detail/components/CreatorsTab/CreatorsControls';
import { SEARCH_DEBOUNCE_MS, useRosterSearch } from '../campaign-detail/hooks/useRosterSearch';
import { useCampaignPageParam } from '../campaign-detail/useCampaignPageParam';
import { PAGE_SIZE_OPTIONS, useCampaignPageSizeParam } from '../campaign-detail/useCampaignPageSizeParam';
import { useDebouncedValue } from '../campaign-detail/hooks/useDebouncedValue';
import { useCampaignVideoControls } from '../campaign-detail/useCampaignVideoControls';
import { useCampaignVideoDeepLink } from '../campaign-detail/useCampaignVideoDeepLink';
import type { CampaignSearch } from '../campaign-detail/search';
import { DataTable } from '../components/common/DataTable';
import { ScreenReaderLabel } from '../components/common/ScreenReaderLabel';
import type { ColumnDef } from '../components/common/DataTable';
import { creatorContentProductsColumns } from './CampaignCreatorContentTab.products';
import { LOW_CONFIDENCE_TEXT, NO_COMMENTS_YET, PERCENT, formatSentimentScore } from './reportFormat';
import { REPORT_SWITCHING_CLASS, isReportLoadFailed, isReportRefreshFailed } from './reportLoadState';
import { ReportRefreshNotice } from './ReportRefreshNotice';
import { lookupOr } from '../lib/lookupOr';
import { cn } from '../lib/utils';

interface CampaignCreatorContentTabProps {
  campaignId?: string;
  campaignPlatform?: string;
  /** First 5 Days Views and #Ad mentioned are in the Excel export only for now; off by default. */
  showFirstFiveAdColumns?: boolean;
}

// One live post, finished by the backend (spend, CPV, engagement rate,
// sentiment band, display status, sound-used state); the tab only formats it.
type CreatorContentEntry = CreatorContentReportRow;

const SENTIMENT_BAND_COLOR: Record<ScoreBand, string> = {
  positive: '#059669',
  mixed: '#D97706',
  negative: '#DC2626',
};
// Hex alpha appended to the band colour for the pill's tinted background.
const SENTIMENT_PILL_BG_ALPHA = '1F';

// The backend caps the interactive page at 500 rows (report_content.go maxContentPageLimit).
const CONTENT_MAX_PAGE_SIZE = 500;
const NO_MATCHES_TITLE = 'No creators match';
const NO_MATCHES_BODY = 'Try a different name or handle.';
// The sort ids the backend accepts; anything else (a stale saved sort) is dropped.
const SERVER_SORT_COLUMNS: ReadonlySet<string> = new Set([
  'creator',
  'engRate',
  'spend',
  'views',
  'first5Views',
  'likes',
  'comments',
  'sound_used',
]);
const UNKNOWN_ERROR_MESSAGE = 'Unknown error';
const CONTENT_UNAVAILABLE_MESSAGE = 'Creator content is unavailable right now.';
// Rows still arrive when the sentiment read fails; without this every "—" in the
// Sentiment column would read as "no comments analysed".
const SENTIMENT_UNAVAILABLE_NOTE = 'Sentiment is unavailable right now.';
const LEFT_OUT_REASON = "are hidden or couldn't be read, so they're left out of totals and engagement rates.";
const EMPTY_RATE = '—';
const NO_FOLLOWERS_NOTE = "Engagement rate is likes plus comments ÷ followers, so it shows once the creator's follower count is known.";
const COUNT_GAP_NOTE: Record<'likes' | 'comments', string> = {
  likes: "Likes are hidden or couldn't be read on the latest update, so this post is left out of engagement rates.",
  comments: "Comments couldn't be read on the latest update, so this post is left out of engagement rates.",
};

// "unavailable" means the backend couldn't read the live posts: show the error
// state rather than "No live content yet".
function contentLoadError(query: {
  isError: boolean;
  error: unknown;
  data: CreatorContentReport | undefined;
  isPlaceholderData: boolean;
}): ApiError | null {
  if (isReportLoadFailed(query)) {
    return new ApiError('UNKNOWN', query.error instanceof Error ? query.error.message : UNKNOWN_ERROR_MESSAGE, 0);
  }
  if (query.data?.status === 'unavailable') return new ApiError('UNKNOWN', CONTENT_UNAVAILABLE_MESSAGE, 0);
  return null;
}

// The backend's display name works in both apps; the `creator` block is admin-only.
const popupCreatorName = (entry: CreatorContentEntry) => entry.creator_name || entry.handle;

function postsPhrase(count: number): string {
  if (count === 1) return '1 post';
  return `${formatIntegerIN(count)} posts`;
}

/** "Likes from 3 posts and comments from 1 post are hidden…": counts the totals had to leave out. */
function leftOutNote(totals: CreatorContentTotals): string | null {
  const likes = totals.likes_left_out ?? 0;
  const comments = totals.comments_left_out ?? 0;
  const parts: string[] = [];
  if (likes > 0) parts.push(`likes from ${postsPhrase(likes)}`);
  if (comments > 0) parts.push(`comments from ${postsPhrase(comments)}`);
  if (parts.length === 0) return null;
  const subject = parts.join(' and ');
  return `${subject.charAt(0).toUpperCase()}${subject.slice(1)} ${LEFT_OUT_REASON}`;
}

function reportNotes(report: CreatorContentReport | undefined): string[] {
  if (!report) return [];
  const notes: string[] = [];
  if (report.sentiment_status === 'unavailable') notes.push(SENTIMENT_UNAVAILABLE_NOTE);
  const leftOut = leftOutNote(report.totals);
  if (leftOut) notes.push(leftOut);
  return notes;
}

function formatRate(percent: number | null): string {
  if (percent === null) return EMPTY_RATE;
  return `${percent.toFixed(DISPLAY_DECIMALS)}%`;
}

/** Why a row has no rate: its creator's following isn't known yet, or its likes or comments weren't read on the latest update. */
function rateNoteFor(entry: CreatorContentEntry): string | null {
  if (entry.followers === null) return NO_FOLLOWERS_NOTE;
  const stats = entry.live_video?.statistics;
  if (!stats) return null;
  if (currentCount(stats, 'likes') === null) return COUNT_GAP_NOTE.likes;
  if (currentCount(stats, 'comments') === null) return COUNT_GAP_NOTE.comments;
  return null;
}

/** The row's rate, with why it is blank when the following is unknown. */
function EngRateCell({ percent, note }: { percent: number | null; note: string | null }) {
  const text = <span className="tabular-nums text-body-sm-semibold text-foreground">{formatRate(percent)}</span>;
  if (!note) return text;
  return <HintTrigger hint={note}>{text}</HintTrigger>;
}

function ReportNotes({ notes }: { notes: string[] }) {
  if (notes.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      {notes.map((note) => (
        <p key={note} className="text-caption-regular text-muted-foreground">
          {note}
        </p>
      ))}
    </div>
  );
}

const PLAY_VIDEO_LABEL = 'play video';

function SentimentPill({ entry }: { entry: CreatorContentEntry }) {
  if (entry.tracking_index != null && entry.sentiment?.comments_since_registration === 0) {
    return <span className="text-caption-regular text-muted-foreground">{NO_COMMENTS_YET}</span>;
  }
  const hasTracking = entry.tracking_index != null;
  const score = hasTracking ? entry.tracking_index : entry.sentiment_index;
  const band = hasTracking ? entry.tracking_band : entry.sentiment_band;
  const color = band ? lookupOr(SENTIMENT_BAND_COLOR, band, '') : '';
  if (score == null || !color) {
    return <span className="text-body-sm-semibold text-muted-foreground">—</span>;
  }
  const isLowConfidence = entry.tracking_low_confidence;
  return (
    <span
      className={cn('inline-flex items-center justify-center rounded-full px-2.5 py-1 text-caption-medium', isLowConfidence && 'opacity-60')}
      style={{ background: `${color}${SENTIMENT_PILL_BG_ALPHA}`, color }}
      title={isLowConfidence ? LOW_CONFIDENCE_TEXT : undefined}
    >
      {formatSentimentScore(score)}
    </span>
  );
}

export const CampaignCreatorContentTab: React.FC<CampaignCreatorContentTabProps> = ({
  campaignId,
  campaignPlatform,
  showFirstFiveAdColumns = false,
}) => {
  const [videoPopup, setVideoPopup] = useState<{ entry: CreatorContentEntry } | null>(null);
  // URL-owned page (same shared ?page= the other roster tabs use) so a hard
  // refresh, tab switch, or browser Back restores the same page — matches
  // Creators/Confirmed/Live/Scripts/Content, which already do this.
  const [page, setPage] = useCampaignPageParam();
  // Column sort is SERVER-side: the backend orders the page it returns, and only
  // the ids in SERVER_SORT_COLUMNS are accepted. Persisted for the session;
  // null = the server's default, most views first. Mirrored in the URL
  // (?sort_by=&sort_dir=, seeded once on mount from a shared link), like the
  // other roster tabs; this tab has no pill/sub-tab dimension, so only one sort.
  const navigate = useNavigate();
  const urlSearch = useSearch({ strict: false }) as Partial<CampaignSearch>;
  const contentSortKey = rosterSortStorageKey('creator-content', campaignId);
  const [sort, setSort] = useState<{ by: string; dir: 'asc' | 'desc' } | null>(() => {
    // Only seed from the URL when it's genuinely pointing at THIS sub-tab —
    // sort_by is a single shared field (like page/pill), so without this guard
    // a fresh mount here would inherit whatever OTHER tab last mirrored its
    // own sort into the URL (e.g. sorting Creators by followers, then
    // navigating to this reporting sub-tab).
    if (urlSearch.sort_by && urlSearch.sub === 'creatorContent') {
      return { by: urlSearch.sort_by, dir: urlSearch.sort_dir ?? 'desc' };
    }
    try {
      const raw = sessionStorage.getItem(contentSortKey);
      return raw ? (JSON.parse(raw) as { by: string; dir: 'asc' | 'desc' }) : null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem(contentSortKey, JSON.stringify(sort));
    } catch {
      /* sessionStorage unavailable */
    }
  }, [sort, contentSortKey]);
  // `replace` (not push): a read-along mirror, not a navigational step Back
  // should undo.
  useEffect(() => {
    void navigate({
      to: '.',
      replace: true,
      search: ((p: Partial<CampaignSearch>) => ({
        ...p,
        sort_by: sort?.by,
        sort_dir: sort?.dir,
      })) as never,
      resetScroll: false,
    } as never);
  }, [sort?.by, sort?.dir, navigate]);
  // URL write-side companion: ?panel=video&video=<roster_entry_id>
  const { openVideo, closeVideo } = useCampaignVideoControls<{ entry: CreatorContentEntry }>(setVideoPopup);

  // Reset to first page when the campaign changes. `setPage` now navigates
  // (URL-owned page), so this is a router side effect — must run in an effect,
  // not during render (adjust-during-render is only safe for React's own
  // setState). The ref guard skips the initial mount so a deep-linked ?page=N
  // survives first load, and only fires on an ACTUAL campaign switch.
  const prevCampaignIdRef = useRef(campaignId);
  useEffect(() => {
    if (prevCampaignIdRef.current === campaignId) return;
    prevCampaignIdRef.current = campaignId;
    setPage(1);
  }, [campaignId, setPage]);

  const [requestedPageSize, setPageSize] = useCampaignPageSizeParam(CONTENT_MAX_PAGE_SIZE);
  // The server pages and sorts; no sort sent means its default, most views first.
  // Search is always the server's: no total is passed, so the hook never filters client-side.
  const resetPage = useCallback(() => setPage(1), [setPage]);
  // The search term is URL-owned (?search=, like page and size) and seeded from
  // the URL only on this sub-tab, for the same reason the sort seed is guarded.
  const videoLabelIdBase = useId();
  const [searchSeed] = useState(() =>
    urlSearch.sub === 'creatorContent' && urlSearch.search ? urlSearch.search : '',
  );
  const searchState = useRosterSearch(undefined, requestedPageSize, { onQueryChange: resetPage, initialQuery: searchSeed });
  const debouncedSearch = useDebouncedValue(searchState.query.trim(), SEARCH_DEBOUNCE_MS);
  const writtenSearchRef = useRef(searchSeed);
  // `replace`: a read-along mirror of the box, like the sort mirror above.
  useEffect(() => {
    if (debouncedSearch === writtenSearchRef.current) return;
    writtenSearchRef.current = debouncedSearch;
    void navigate({
      to: '.',
      replace: true,
      search: ((p: Partial<CampaignSearch>) => ({ ...p, search: debouncedSearch || undefined })) as never,
      resetScroll: false,
    } as never);
  }, [debouncedSearch, navigate]);
  const params: CreatorContentParams = {
    page,
    limit: requestedPageSize,
    ...(sort ? { sort_by: sort.by, sort_dir: sort.dir } : {}),
    ...(searchState.serverSearch ? { search: searchState.serverSearch } : {}),
  };
  const query = useCreatorContentReport(campaignId ?? '', params);
  const { data: report, isLoading, refetch } = query;
  const pageRows = report?.rows ?? [];
  const totalRows = report?.total ?? 0;
  const isSwitching = query.isPlaceholderData;
  // The response's limit is what the server actually paged by; footer and pages follow it.
  const pageSize = report?.limit ?? requestedPageSize;

  // URL → state reader. Video deep-link (?panel=video&video=<entry_id>) resolves
  // against the CURRENT page. Under server-side pagination a target on another
  // page won't auto-open from the link (the row is still clickable). There's no
  // server entry-id→page resolver. Open-once guard prevents close→reopen.
  const { videoId: deepLinkVideoId, row: deepLinkVideoRow } = useCampaignVideoDeepLink(
    pageRows,
    (e) => e.id,
  );
  const videoDeepLinkOpenedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!deepLinkVideoId || !deepLinkVideoRow) return;
    if (videoDeepLinkOpenedFor.current === deepLinkVideoId) return;
    videoDeepLinkOpenedFor.current = deepLinkVideoId;
    setVideoPopup({ entry: deepLinkVideoRow });
  }, [deepLinkVideoId, deepLinkVideoRow]);

  // Leaf value accessors for the cells and the video popup.
  const countOf = (e: CreatorContentEntry, kind: CountKind): CountDisplay => describeCount(e.live_video?.statistics, kind);
  const spendOf = (e: CreatorContentEntry): number | null => e.spend;
  const engRateOf = (e: CreatorContentEntry): number | null =>
    e.engagement_rate === null ? null : e.engagement_rate * PERCENT;

  // Column visibility follows the server flag, the same one the Excel export uses,
  // so the column does not flicker between pages. Each cell reads its own row's rate.
  const showEngRate = report?.columns.engagement_rate ?? false;
  const showSoundUsed = report?.columns.sound_used ?? false;
  const showStatus = report?.columns.status ?? false;
  const showProducts = report?.columns.products ?? false;

  // A stale or deep-linked sort on a column the report hides would reorder rows with no
  // visible indicator, so drop it during render once the report says the column is gone.
  const isSortHidden =
    sort !== null &&
    report !== undefined &&
    !isSwitching &&
    (!SERVER_SORT_COLUMNS.has(sort.by) ||
      (sort.by === 'engRate' && !showEngRate) ||
      (sort.by === 'sound_used' && !showSoundUsed) ||
      (sort.by === 'first5Views' && !showFirstFiveAdColumns));
  if (isSortHidden) setSort(null);

  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  // A page past the end comes back empty with the real total: step back to the last page.
  // A total that fell to zero (rows removed, or a search that matched nothing) has no
  // page to stay on: back to page 1.
  useEffect(() => {
    if (!report || isSwitching) return;
    if (totalRows === 0 && page > 1) setPage(1);
    else if (report.rows.length === 0 && totalRows > 0 && page > totalPages) setPage(totalPages);
  }, [report, isSwitching, totalRows, page, totalPages, setPage]);

  const apiError = contentLoadError(query);

  const columns = useMemo((): ColumnDef<CreatorContentEntry>[] => {
    const cols: ColumnDef<CreatorContentEntry>[] = [
      {
        id: 'creator',
        sortable: true,
        header: 'Creator',
        sticky: 'left',
        // Fixed — matches the Creator column elsewhere in campaign-detail.
        minWidth: 320,
        maxWidth: 340,
        grow: true,
        cell: (entry) => (
          <PlatformCreatorCell creator={entry} campaignPlatform={campaignPlatform} />
        ),
      },
      {
        id: 'sentiment',
        header: 'Sentiment',
        // Header trimmed from "Sentiment Score": the longer text forced the
        // column to ~131px (cell needs ~80px), pushing the table into h-scroll.
        minWidth: 84,
        cell: (entry) => <SentimentPill entry={entry} />,
      },
    ];

    // Read-only product chips after the Creator column (returns [] when the
    // report hides the column).
    cols.splice(1, 0, ...creatorContentProductsColumns(showProducts));

    if (showEngRate) {
      cols.push({
        id: 'engRate',
        sortable: true,
        header: 'Eng. Rate',
        minWidth: 80,
        align: 'right',
        cell: (entry) => <EngRateCell percent={engRateOf(entry)} note={rateNoteFor(entry)} />,
      });
    }

    cols.push(
      {
        id: 'spend',
        sortable: true,
        header: 'Spend',
        minWidth: 132,
        align: 'right',
        cell: (entry) => {
          const spend = formatMoneyExact(spendOf(entry));
          return (
            <span className={`tabular-nums text-body-sm-semibold text-foreground ${moneyFitClass(spend)}`}>
              {spend}
            </span>
          );
        },
      },
      {
        id: 'views',
        sortable: true,
        header: 'Views',
        minWidth: 80,
        align: 'right',
        cell: (entry) => <EngagementCountCell count={countOf(entry, 'views')} className={COUNT_CELL_CLASS} />,
      },
      ...(showFirstFiveAdColumns
        ? [
            {
              id: 'first5Views',
              sortable: true,
              header: 'First 5 Days Views',
              minWidth: 150,
              align: 'right' as const,
              cell: (entry: CreatorContentEntry) => <FirstFiveDaysCell cell={entry.first_five_days_views} />,
            },
          ]
        : []),
      {
        id: 'cpv',
        header: 'CPV',
        minWidth: 80,
        align: 'right',
        cell: (entry) => (
          <span className="tabular-nums text-body-sm-semibold text-foreground">
            {/* TODO(currency): pass the campaign's real currency once multi-currency
                is rebuilt end-to-end; formatCpv is INR-only for now (see format.ts). */}
            {formatCpv(entry.cpv)}
          </span>
        ),
      },
      {
        id: 'likes',
        sortable: true,
        header: 'Likes',
        minWidth: 80,
        align: 'right',
        cell: (entry) => <EngagementCountCell count={countOf(entry, 'likes')} className={COUNT_CELL_MUTED_CLASS} />,
      },
      {
        id: 'comments',
        sortable: true,
        header: 'Comments',
        minWidth: 84,
        align: 'right',
        cell: (entry) => <EngagementCountCell count={countOf(entry, 'comments')} className={COUNT_CELL_MUTED_CLASS} />,
      },
      {
        id: 'content',
        header: 'Content',
        // Matches the Live Content table's width; wrap lets the YT title reach
        // line-clamp-3, grow shares slack with Creator. YouTube gets a wider
        // column (it renders a title); others stay narrow.
        minWidth: campaignPlatform === 'youtube' ? 280 : 180,
        grow: true,
        wrap: true,
        cell: (entry) => {
          const thumb = liveVideoThumbnailUrl(entry.live_video);
          const creatorName = popupCreatorName(entry);
          return (
            <div
              role="button"
              tabIndex={0}
              aria-labelledby={`${videoLabelIdBase}-${entry.id}`}
              className="flex cursor-pointer items-center gap-3"
              onClick={() => openVideo({ entry }, entry.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openVideo({ entry }, entry.id);
                }
              }}
            >
              <div className="relative h-[54px] w-24 shrink-0 overflow-hidden rounded-lg bg-muted">
                {thumb ? (
                  <img
                    src={thumb}
                    alt=""
                    className="size-full object-cover"
                    referrerPolicy="no-referrer"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex size-full items-center justify-center text-muted-foreground">
                    <Play size={18} />
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/20 opacity-0 transition-opacity hover:opacity-100">
                  <Play size={20} className="fill-white text-white" />
                </div>
              </div>
              {/* YT-only: IG videos have no title. */}
              {campaignPlatform === 'youtube' && entry.live_video?.title && (
                <div className="line-clamp-3 min-w-0 flex-1 text-caption-medium leading-snug text-foreground">
                  {entry.live_video.title}
                </div>
              )}
              <ScreenReaderLabel id={`${videoLabelIdBase}-${entry.id}`}>
                {creatorName}, {PLAY_VIDEO_LABEL}
              </ScreenReaderLabel>
            </div>
          );
        },
      },
    );

    // Post Liveness "Status" (Instagram campaigns), right after Creator so the
    // visibility of the post reads before its numbers.
    if (showStatus) {
      cols.splice(1, 0, {
        id: 'liveness',
        header: 'Status',
        minWidth: 120,
        cell: (entry) => <LivenessStatusPill state={entry.display_status} />,
      });
    }

    // Official Audio Compliance "Sound used" (Instagram campaigns that received a
    // song at creation). Placed right before Content so it sits in the same
    // position as on the Live tab. `sound_used` still supplies the song name and
    // cover art.
    if (showSoundUsed) {
      cols.splice(cols.length - 1, 0, {
        id: 'sound_used',
        sortable: true,
        header: 'Sound used',
        minWidth: 200,
        cell: (entry) => <SoundUsedCell row={{ sound_used: entry.sound_used }} state={entry.sound_used_state} />,
      });
    }

    // Before Content and after Sound used, as in the export.
    if (showFirstFiveAdColumns) {
      cols.splice(cols.length - 1, 0, {
        id: 'adMentioned',
        header: '#Ad mentioned',
        minWidth: 120,
        cell: (entry) => <AdMentionedCell cell={entry.ad_disclosure} />,
      });
    }

    return cols;
  // Column defs depend only on the listed view flags; the cell renderers close
  // over stable callbacks (openVideo, sort handlers) intentionally excluded so
  // the defs don't rebuild every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoLabelIdBase, showEngRate, showProducts, showStatus, showSoundUsed, campaignPlatform, showFirstFiveAdColumns]);

  const noMatches = report?.status !== 'no_data' && Boolean(params.search);
  const emptyState = (
    <div className="flex flex-col items-center justify-center gap-3">
      <Play size={36} className="text-muted-foreground" />
      <p className="text-body-sm-semibold text-muted-foreground">
        {noMatches ? NO_MATCHES_TITLE : 'No live content yet'}
      </p>
      <p className="text-caption-regular text-muted-foreground">
        {noMatches ? NO_MATCHES_BODY : 'Once creators go live with their content, they will appear here.'}
      </p>
    </div>
  );
  const showSearch = report?.status !== 'no_data' || searchState.isSearching;

  return (
    <div className={`flex flex-col gap-2 ${isSwitching ? REPORT_SWITCHING_CLASS : ''}`} aria-busy={isSwitching}>
      {showSearch && (
        <div className="flex">
          <CreatorsControls query={searchState.query} onQueryChange={searchState.setQuery} />
        </div>
      )}
      <DataTable
        columns={columns}
        data={pageRows}
        getRowId={(e) => e.id}
        sort={{
          by: sort?.by ?? null,
          dir: sort?.dir ?? 'desc',
          onChange: ({ by, dir }) => {
            setSort(by ? { by, dir } : null);
            setPage(1);
          },
        }}
        loading={isLoading}
        error={apiError}
        onRetry={() => refetch()}
        emptyState={emptyState}
        pagination={
          !isLoading && !apiError && (totalRows > 0 || searchState.isSearching)
            ? {
                page,
                pageSize,
                total: totalRows,
                onPageChange: setPage,
                noun: 'creators',
                pageSizeOptions: PAGE_SIZE_OPTIONS,
                maxPageSize: CONTENT_MAX_PAGE_SIZE,
                onPageSizeChange: setPageSize,
                keepWhenEmpty: searchState.isSearching,
              }
            : undefined
        }
      />
      {isReportRefreshFailed(query) && <ReportRefreshNotice onRetry={() => void refetch({ cancelRefetch: false })} />}
      <ReportNotes notes={reportNotes(report)} />

      {/* ── Video popup ────────────────────────────────────────────────────── */}
      {videoPopup && (() => {
        const videoUrl = videoPopup.entry.live_video?.url ?? '';
        const thumb = liveVideoThumbnailUrl(videoPopup.entry.live_video) ?? undefined;
        return (
          <VideoPlayerPopup
            videoUrl={videoUrl}
            thumbnailUrl={thumb}
            creatorName={popupCreatorName(videoPopup.entry)}
            creatorHandle={videoPopup.entry.handle}
            creatorAvatarUrl={videoPopup.entry.platform?.profile_picture_url}
            platform={campaignPlatform ?? 'youtube'}
            views={countOf(videoPopup.entry, 'views')}
            likes={countOf(videoPopup.entry, 'likes')}
            comments={countOf(videoPopup.entry, 'comments')}
            onClose={closeVideo}
          />
        );
      })()}
    </div>
  );
};
